/*
 * This is a reimplementation of what exists in our HTML template files
 * already. Once the React tree mounts, this is what gets rendered first, until
 * the app is ready to go.
 *
 * NOTE: this file deliberately imports *nothing* from `#/alf`. It is the very
 * first thing React renders on web, and the ALF barrel is entangled in a
 * cycle - `#/alf` re-exports `atoms`, which imports `#/components/Layout`,
 * which pulls in `#/components/Dialog` and `#/state/dialogs` - so a hoisted
 * barrel getter such as `flatten` can still be `undefined` when we reach for
 * it, which throws on first paint. Bundling a handful of atoms through that
 * barrel bought us nothing and cost us a boot crash, so the styles below are
 * the flattened result of those atoms, written out. Keep it that way: if the
 * splash needs another style, add it here rather than importing ALF.
 */

import {useEffect, useRef, useState} from 'react'
import Svg, {Path} from 'react-native-svg'

const size = 100
const ratio = 92 / 92

export function Splash({
  isReady,
  children,
}: React.PropsWithChildren<{
  isReady: boolean
}>) {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)
  const splashRef = useRef<HTMLDivElement>(null)

  // hide the static one that's baked into the HTML - gets replaced by our React version below
  useEffect(() => {
    // double rAF ensures that the React version gets painted first
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const splash = document.getElementById('splash')
        if (splash) {
          splash.remove()
        }
      })
    })
  }, [])

  // when ready, we fade/scale out
  useEffect(() => {
    if (!isReady) return

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    const node = splashRef.current
    if (!node || reduceMotion) {
      setIsAnimationComplete(true)
      return
    }

    const animation = node.animate(
      [
        {opacity: 1, transform: 'scale(1)'},
        {opacity: 0, transform: 'scale(1.5)'},
      ],
      {
        duration: 300,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        fill: 'forwards',
      },
    )
    animation.onfinish = () => setIsAnimationComplete(true)

    return () => {
      animation.cancel()
    }
  }, [isReady])

  return (
    <>
      {isReady && children}

      {!isAnimationComplete && (
        <div
          ref={splashRef}
          style={{
            // a.fixed + a.inset_0
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // a.flex + a.align_center + a.justify_center
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // to compensate for the `top: -50px` below
            transformOrigin: 'center calc(50% - 50px)',
          }}>
          <Svg
            fill="none"
            viewBox="0 0 92 92"
            style={{
              position: 'relative',
              width: size,
              height: size * ratio,
              top: -50,
            }}>
            <Path
              fill="#006AFF"
              d="M91.996 45.2164C91.996 38.7248 86.947 33.2637 80.5585 32.5425H79.0129C72.6245 32.9546 67.5755 37.7975 67.2664 44.186C67.2664 44.5294 67.2664 44.8729 67.2664 45.2164V45.5255V45.8346C67.2664 59.745 56.4472 71.0794 42.846 71.3885C29.1417 71.3885 18.1164 60.3632 17.8073 46.4528C17.8073 42.3312 14.3039 38.7248 10.1823 38.7248C6.06072 38.7248 2.55737 42.3312 2.55737 46.4528V47.1741C2.86649 72.3158 23.1653 92.3056 47.8949 91.9965C72.6245 91.6873 92.3051 71.0794 91.996 45.8346V45.2164ZM91.996 13.274C91.996 19.9716 86.6379 25.8449 79.6312 25.8449C72.6245 25.8449 67.2664 20.3838 67.2664 13.274C67.2664 6.16425 72.6245 0.600098 79.6312 0.600098C86.6379 0.600098 91.996 6.16425 91.996 13.274ZM19.0437 24.8145C19.0437 30.0695 14.9222 34.1911 9.8732 34.1911C4.7212 34.1911 0.599609 30.0695 0.599609 24.8145C0.599609 19.6625 4.7212 15.5409 9.8732 15.5409C14.9222 15.5409 19.0437 19.6625 19.0437 24.8145Z"
            />
          </Svg>
        </div>
      )}
    </>
  )
}
